/*
 * 1DS JS SDK Privacy Guard plugin, 4.0.4
 * Copyright (c) Microsoft and contributors. All rights reserved.
 * (Microsoft Internal Only)
 */
import { __extendsFn as __extends } from "@microsoft/applicationinsights-shims";
/**
* PrivacyGuardPlugin.ts
* @author Sid Dahiya (sidahiy)
* @copyright Microsoft 2020
* File containing the implementation for Privacy Guard plugin.
*/
import dynamicProto from "@microsoft/dynamicproto-js";
import { BaseTelemetryPlugin, _throwInternal, arrForEach, doPerf, isNullOrUndefined, isObject, isString, objForEachKey, onConfigChange, createProcessTelemetryContext } from "@microsoft/1ds-core-js";
import { PrivacyGuardInternal } from "./PrivacyGuardInternal";
import { objDeepFreeze } from "@nevware21/ts-utils";
var undefValue = undefined;
var defautPrivacyGuardConfig = objDeepFreeze({
    context: undefValue,
    notificationEventName: undefValue,
    summaryEventName: undefValue,
    iKey: undefValue,
    useEventFieldPrefix: true,
    useAbsoluteFieldName: false,
    enabled: true,
    scanForUrls: true,
    disableAdvancedScans: false
});
var PrivacyGuardPlugin = /** @class */ (function (_super) {
    __extends(PrivacyGuardPlugin, _super);
    function PrivacyGuardPlugin() {
        var _this = _super.call(this) || this;
        _this.identifier = "PrivacyGuardPlugin";
        _this.priority = 499;
        _this.version = '4.0.4';
        // Manually set configuration that is not reset during teardown / unload
        var _manualConfig = null;
        var _manualEnabled = null;
        var _manualCommonContext = null;
        var _ignoredConcerns = [];
        // Automatic defaults set during construction and reset during teardown / unload via _initDefaults()
        var _pgInternal;
        var _core;
        var _iKey;
        var _notificationEventName;
        var _summaryEventName;
        var _useFieldPrefix;
        var _useAbsoluteFieldName;
        var _concernsNotified;
        var _eventsIgnored;
        var _eventsInspected;
        var _fieldsInspected;
        dynamicProto(PrivacyGuardPlugin, _this, function (_self, _base) {
            _initDefaults();
            _self.initialize = function (coreConfig, core, extensions) {
                _base.initialize(coreConfig, core, extensions);
                var configuration = null;
                _self._addHook(onConfigChange(coreConfig, function (details) {
                    var theConfig = details.cfg;
                    var ctx = createProcessTelemetryContext(null, theConfig, core);
                    configuration = ctx.getExtCfg(_self.identifier, defautPrivacyGuardConfig);
                    if (_self.isEnabled() && configuration.iKey && isString(configuration.iKey)) {
                        _iKey = configuration.iKey; // make sure it didn't apply on initial call
                    }
                }));
                if (!configuration) {
                    _throwInternal(core.logger, 1 /* eLoggingSeverity.CRITICAL */, 73 /* _eInternalMessageId.PluginException */, 'Configuration not provided, PrivacyGuard disabled.');
                    _base.setInitialized(false);
                }
                else {
                    _core = core;
                    initInternal(_manualConfig || configuration);
                    // Reapply any manually set values
                    if (!isNullOrUndefined(_manualEnabled)) {
                        _self.setEnabled(_manualEnabled);
                    }
                    if (!isNullOrUndefined(_manualCommonContext)) {
                        _self.updateCommonContext(_manualCommonContext); // manual config will overide previous changes
                    }
                }
            };
            //Test hooks
            _self["_getDbgPlgTargets"] = function () {
                return [_iKey];
            };
            _self.reset = function (configuration) {
                _manualConfig = configuration;
                if (_manualConfig) { // Support "clearing" as this is not reset during unloading
                    initInternal(_manualConfig);
                }
            };
            function initInternal(configuration) {
                _pgInternal = new PrivacyGuardInternal();
                _pgInternal.initialize(configuration);
                if (configuration.notificationEventName && isString(configuration.notificationEventName)) {
                    _notificationEventName = configuration.notificationEventName;
                }
                if (configuration.summaryEventName && isString(configuration.summaryEventName)) {
                    _summaryEventName = configuration.summaryEventName;
                }
                if (configuration.iKey && isString(configuration.iKey)) {
                    _iKey = configuration.iKey;
                }
                _pgInternal.setEnabled(configuration.enabled !== false);
                _useFieldPrefix = configuration.useEventFieldPrefix !== false;
                _useAbsoluteFieldName = configuration.useAbsoluteFieldName === true;
            }
            _self.isEnabled = function () {
                return _pgInternal && _pgInternal.isEnabled();
            };
            _self.isPrivacyGuardEnabled = function () {
                return _self.isEnabled();
            };
            _self.setEnabled = function (enabled) {
                _manualEnabled = enabled;
                return _pgInternal && _pgInternal.setEnabled(enabled);
            };
            _self.updateCommonContext = function (freshCommonContext) {
                _manualCommonContext = freshCommonContext;
                if (_pgInternal) {
                    _pgInternal.updateCommonContext(_manualCommonContext);
                }
            };
            _self.processTelemetry = function (event, itemCtx) {
                doPerf(_core, function () { return "PrivacyGuard:_processTelemetry"; }, function () {
                    if (_self.isEnabled() && event.name !== _notificationEventName && event.name !== _summaryEventName) {
                        if (event.data) {
                            _eventsInspected++;
                            var baseType;
                            // Office Telemetry JS SDK uses event.baseType, while CS 2.0 used event.data.baseType.
                            if (isString(event.baseType)) {
                                baseType = event.baseType;
                            }
                            else if (event.data && event.data.baseType && isString(event.data.baseType)) {
                                baseType = event.data.baseType;
                            }
                            var concerns = processObject(event.name, baseType, "", event.data);
                            if (concerns.length > 0) {
                                _self.notifyConcerns(concerns);
                            }
                        }
                        else {
                            // Account for any event who's data might be empty.
                            // We do not count Summary and Notification event in this metric.
                            _eventsIgnored++;
                        }
                    }
                });
                _self.processNext(event, itemCtx);
            };
            function processObject(eventName, baseTypeName, parentKey, toProcess) {
                if (!_self.isEnabled()) {
                    return [];
                }
                var concerns = [];
                if (isNullOrUndefined(toProcess)) {
                    return concerns;
                }
                objForEachKey(toProcess, function (propKey, theValue) {
                    var fieldName = propKey;
                    if (_useAbsoluteFieldName && parentKey && parentKey.length > 0) {
                        fieldName = parentKey + "." + propKey;
                    }
                    if (isString(theValue)) {
                        _fieldsInspected++;
                        concerns = concerns.concat(_pgInternal.getAllPrivacyConcerns(eventName, baseTypeName, fieldName, theValue));
                    }
                    else if (theValue && isObject(theValue)) {
                        concerns = concerns.concat(processObject(eventName, baseTypeName, fieldName, theValue));
                    }
                });
                return concerns;
            }
            _self.sendSummaryEvent = function (shouldResetSummary) {
                var pgEvent = {
                    name: _summaryEventName,
                    ext: {
                        metadata: {
                            privTags: 33554432,
                            privLevel: 110
                        }
                    }
                };
                pgEvent.data = getStatsInternal(_useFieldPrefix);
                if (_iKey) {
                    pgEvent.iKey = _iKey;
                }
                _core.track(pgEvent);
                if (shouldResetSummary) {
                    _concernsNotified = 0;
                    _eventsIgnored = 0;
                    _eventsInspected = 0;
                    _fieldsInspected = 0;
                }
            };
            function IsIgnoredConcern(concern) {
                var isIgnoredConcern = false;
                arrForEach(_ignoredConcerns, function (ignoredConcern) {
                    if (concern.Concern === ignoredConcern.Concern &&
                        concern.EventName === ignoredConcern.EventName &&
                        concern.FieldName === ignoredConcern.FieldName &&
                        ((concern.BaseType === ignoredConcern.BaseType) ||
                            (!concern.BaseType || !ignoredConcern.BaseType))) {
                        isIgnoredConcern = true;
                    }
                });
                return isIgnoredConcern;
            }
            _self.addIgnoredConcern = function (concern) {
                if (!IsIgnoredConcern(concern)) {
                    _ignoredConcerns.push(concern);
                }
            };
            _self.addIgnoredConcerns = function (concerns) {
                if (concerns.length === 0) {
                    return;
                }
                arrForEach(concerns, function (concern) {
                    _self.addIgnoredConcern(concern);
                });
            };
            _self.notifyConcerns = function (concerns) {
                arrForEach(concerns, function (concern) {
                    if (!IsIgnoredConcern(concern)) {
                        var pgEvent = {
                            name: _notificationEventName,
                            ext: {
                                metadata: {
                                    privTags: 33554432,
                                    privLevel: 110
                                }
                            }
                        };
                        if (_useFieldPrefix) {
                            pgEvent.data = {
                                PG_EventName: concern.EventName,
                                PG_FieldName: concern.FieldName,
                                PG_TypeAsText: DataConcernTypeToString(concern.Concern),
                                PG_BaseType: concern.BaseType
                            };
                        }
                        else {
                            pgEvent.data = {
                                EventName: concern.EventName,
                                FieldName: concern.FieldName,
                                TypeAsText: DataConcernTypeToString(concern.Concern),
                                BaseType: concern.BaseType
                            };
                        }
                        if (_iKey) {
                            pgEvent.iKey = _iKey;
                        }
                        _core.track(pgEvent);
                        _concernsNotified++;
                    }
                });
            };
            _self._doTeardown = function (unloadCtx, unloadState) {
                _initDefaults();
            };
            _self.getStats = function () {
                return getStatsInternal(false);
            };
            function getStatsInternal(useFieldPrefix) {
                if (useFieldPrefix) {
                    return {
                        PG_ConcernsNotified: _concernsNotified,
                        PG_EventsIgnored: _eventsIgnored,
                        PG_EventsInspected: _eventsInspected,
                        PG_FieldsInspected: _fieldsInspected
                    };
                }
                else {
                    return {
                        ConcernsNotified: _concernsNotified,
                        EventsIgnored: _eventsIgnored,
                        EventsInspected: _eventsInspected,
                        FieldsInspected: _fieldsInspected
                    };
                }
            }
            function _initDefaults() {
                _pgInternal = null;
                _core = null;
                _iKey = null;
                _notificationEventName = "PrivacyConcern";
                _summaryEventName = "PrivacyGuardSummary";
                _useFieldPrefix = true;
                _useAbsoluteFieldName = false;
                _concernsNotified = 0;
                _eventsIgnored = 0;
                _eventsInspected = 0;
                _fieldsInspected = 0;
            }
            function DataConcernTypeToString(type) {
                switch (type) {
                    case 0 /* DataConcernType.None */:
                        return "None";
                    case 6 /* DataConcernType.FieldNameImpliesLocation */:
                        return "FieldNameImpliesLocation";
                    case 11 /* DataConcernType.InternalEmailAddress */:
                        return "InternalEmailAddress";
                    case 15 /* DataConcernType.OutOfScopeIdentifier */:
                        return "OutOfScopeIdentifier";
                    case 14 /* DataConcernType.MachineName */:
                        return "MachineName";
                    case 20 /* DataConcernType.UserDomain */:
                        return "UserDomain";
                    case 12 /* DataConcernType.IpAddress */:
                        return "IPAddress";
                    case 5 /* DataConcernType.ExternalEmailAddress */:
                        return "ExternalEmailAddress";
                    case 21 /* DataConcernType.UserName */:
                        return "UserName";
                    case 19 /* DataConcernType.UserAlias */:
                        return "UserAlias";
                    case 4 /* DataConcernType.Directory */:
                        return "Directory";
                    case 18 /* DataConcernType.Url */:
                        return "Url";
                    case 7 /* DataConcernType.FileNameOrExtension */:
                        return "FileNameOrExtension";
                    case 1 /* DataConcernType.Content */:
                        return "Content";
                    case 8 /* DataConcernType.FileSharingUrl */:
                        return "FileSharingUrl";
                    case 17 /* DataConcernType.Security */:
                        return "Security";
                    case 22 /* DataConcernType.AccessToken */:
                        return "AccessToken";
                    case 23 /* DataConcernType.Signature */:
                        return "Signature";
                    default:
                        break;
                }
                return "Missing String value for " + type;
            }
        });
        return _this;
    }
    /**
     * Starts the Privacy Guard plugin
     * @param config The core configuration.
     */
    PrivacyGuardPlugin.prototype.initialize = function (coreConfig, core, extensions) {
        // Make sure we have a config definition for this identifier
    };
// Removed Stub for PrivacyGuardPlugin.prototype.processTelemetry.
// Removed Stub for PrivacyGuardPlugin.prototype.updateCommonContext.
// Removed Stub for PrivacyGuardPlugin.prototype.isEnabled.
// Removed Stub for PrivacyGuardPlugin.prototype.isPrivacyGuardEnabled.
// Removed Stub for PrivacyGuardPlugin.prototype.setEnabled.
// Removed Stub for PrivacyGuardPlugin.prototype.sendSummaryEvent.
// Removed Stub for PrivacyGuardPlugin.prototype.reset.
// Removed Stub for PrivacyGuardPlugin.prototype.getStats.
// Removed Stub for PrivacyGuardPlugin.prototype.addIgnoredConcern.
// Removed Stub for PrivacyGuardPlugin.prototype.addIgnoredConcerns.
// Removed Stub for PrivacyGuardPlugin.prototype.notifyConcerns.
    return PrivacyGuardPlugin;
}(BaseTelemetryPlugin));
export { PrivacyGuardPlugin };
//# sourceMappingURL=PrivacyGuardPlugin.js.map